import { getTailWindColors } from '@/utils/common';

import {
  fontTypesMap,
  fontWeightMap,
  LINE_TYPES,
  lineTypesMap,
} from '../config';

export const CUSTOMIZE_PREPENDED_ID = 'acres-draw';

export enum CustomizedLayerIds {
  FENCE_A = `${CUSTOMIZE_PREPENDED_ID}-fence-a`,
  FENCE_B = `${CUSTOMIZE_PREPENDED_ID}-fence-b`,
  PATH = `${CUSTOMIZE_PREPENDED_ID}-path`,
  PIPELINE_A = `${CUSTOMIZE_PREPENDED_ID}-pipeline-a`,
  PIPELINE_B = `${CUSTOMIZE_PREPENDED_ID}-pipeline-b`,
  TRACK_A = `${CUSTOMIZE_PREPENDED_ID}-track-a`,
  TRACK_B = `${CUSTOMIZE_PREPENDED_ID}-track-b`,
  BASE_LINE = `${CUSTOMIZE_PREPENDED_ID}-base-line-customize`,
  BASE_POLYGON = `${CUSTOMIZE_PREPENDED_ID}-base-polygon-customize`,
  BASE_POLYGON_LINE = `${CUSTOMIZE_PREPENDED_ID}-base-polygon-line-customize`,
}

const { 'customize-swatch': customColor } = getTailWindColors();

const appendUser = (shouldAppendUser: boolean, value: string) =>
  shouldAppendUser ? `user_${value}` : value;

const linePaintForSpecialLines = (isDrawToolLayer: boolean) => ({
  'line-color': [
    'coalesce',
    ['get', appendUser(isDrawToolLayer, 'border-color')],
    customColor.white,
  ],
  'line-opacity': [
    'coalesce',
    ['get', appendUser(isDrawToolLayer, 'border-opacity')],
    1,
  ],
});

export const baseLinePaint = (isDrawLineLayer: boolean) => ({
  'line-color': [
    'coalesce',
    ['get', appendUser(isDrawLineLayer, 'border-color')],
    customColor.white,
  ],
  // 'line-dasharray': [
  //   'case',
  //   [
  //     'all',
  //     ['has', appendUser(isDrawLineLayer, 'border-pattern')],
  //     [
  //       '!=',
  //       ['get', appendUser(isDrawLineLayer, 'border-pattern')],
  //       lineTypesMap[LINE_TYPES.DOUBLE],
  //     ],
  //     [
  //       '!=',
  //       ['typeof', ['get', appendUser(isDrawLineLayer, 'border-pattern')]],
  //       'object',
  //     ],
  //     ['!=', ['get', appendUser(isDrawLineLayer, 'border-pattern')], null], // Check if border-pattern is not null
  //   ],
  //   ['get', appendUser(isDrawLineLayer, 'border-pattern')],
  //   ['literal', lineTypesMap[LINE_TYPES.SOLID]],
  // ],
  'line-gap-width': [
    'case',
    [
      'all',
      ['has', appendUser(isDrawLineLayer, 'border-pattern')],
      [
        '==',
        ['get', appendUser(isDrawLineLayer, 'border-pattern')],
        lineTypesMap[LINE_TYPES.DOUBLE],
      ], // If border-pattern does equal DOUBLE, provide a double line
    ],
    2,
    0,
  ],
  'line-opacity': [
    'coalesce',
    ['get', appendUser(isDrawLineLayer, 'border-opacity')],
    1,
  ],
  'line-width': ['coalesce', ['get', appendUser(isDrawLineLayer, 'size')], 2],
});

export const baseLinePolygonPaint = (isDrawLineLayer: boolean) => ({
  'line-color': [
    'coalesce',
    ['get', appendUser(isDrawLineLayer, 'border-color')],
    customColor.white,
  ],
  // 'line-dasharray': [
  //   'case',
  //   [
  //     'all',
  //     ['has', appendUser(isDrawLineLayer, 'border-pattern')],
  //     [
  //       '!=',
  //       ['get', appendUser(isDrawLineLayer, 'border-pattern')],
  //       lineTypesMap[LINE_TYPES.DOUBLE],
  //     ],
  //     [
  //       '!=',
  //       ['typeof', ['get', appendUser(isDrawLineLayer, 'border-pattern')]],
  //       'object',
  //     ],
  //     ['!=', ['get', appendUser(isDrawLineLayer, 'border-pattern')], null], // Check if border-pattern is not null
  //   ],
  //   ['get', appendUser(isDrawLineLayer, 'border-pattern')],
  //   ['literal', lineTypesMap[LINE_TYPES.SOLID]],
  // ],
  'line-gap-width': [
    'case',
    [
      'all',
      ['has', appendUser(isDrawLineLayer, 'border-pattern')],
      [
        '==',
        ['get', appendUser(isDrawLineLayer, 'border-pattern')],
        lineTypesMap[LINE_TYPES.DOUBLE],
      ], // If pattern does equal DOUBLE, provide a double line
    ],
    2,
    0,
  ],
  'line-opacity': [
    'coalesce',
    ['get', appendUser(isDrawLineLayer, 'border-opacity')],
    1,
  ],
  'line-width': ['coalesce', ['get', appendUser(isDrawLineLayer, 'size')], 4],
});

export const fenceALayer = (isDrawLineLayer: boolean) => ({
  id: CustomizedLayerIds.FENCE_A,
  layout: { 'line-miter-limit': 0 },
  paint: {
    ...linePaintForSpecialLines(isDrawLineLayer),
    'line-gap-width': 3,
    'line-width': 2,
  },
  type: 'line',
});

export const fenceBLayer = (isDrawLineLayer: boolean) => ({
  id: CustomizedLayerIds.FENCE_B,
  layout: { 'line-miter-limit': 0 },
  paint: {
    ...linePaintForSpecialLines(isDrawLineLayer),
    'line-dasharray': [0.1, 1],
    'line-width': 10,
  },
  type: 'line',
});

export const trackALayer = (isDrawLineLayer: boolean) => ({
  id: CustomizedLayerIds.TRACK_A,
  layout: { 'line-miter-limit': 0 },
  paint: {
    ...linePaintForSpecialLines(isDrawLineLayer),
    'line-width': 2,
  },
  type: 'line',
});

export const trackBLayer = (isDrawLineLayer: boolean) => ({
  id: CustomizedLayerIds.TRACK_B,
  layout: { 'line-join': 'miter', 'line-miter-limit': 0 },
  paint: {
    ...linePaintForSpecialLines(isDrawLineLayer),
    'line-dasharray': [0.1, 2],
    'line-width': 10,
  },
  type: 'line',
});

export const pipelineALayer = (isDrawLineLayer: boolean) => ({
  id: CustomizedLayerIds.PIPELINE_A,
  layout: { 'line-miter-limit': 0 },
  paint: { ...linePaintForSpecialLines(isDrawLineLayer), 'line-width': 6 },
  type: 'line',
});

export const pipelineBLayer = (isDrawLineLayer: boolean) => ({
  id: CustomizedLayerIds.PIPELINE_B,
  layout: {
    'line-cap': 'round',
    'line-join': 'round',
    'line-miter-limit': 0,
  },
  paint: {
    ...linePaintForSpecialLines(isDrawLineLayer),
    'line-color': customColor.white,
    'line-dasharray': [0, 3],
    'line-width': 2,
  },
  type: 'line',
});

export const pathLayer = (isDrawLineLayer: boolean) => ({
  id: CustomizedLayerIds.PATH,
  layout: { 'line-join': 'bevel', 'line-miter-limit': 0 },
  paint: {
    ...linePaintForSpecialLines(isDrawLineLayer),
    'line-dasharray': [1, 1],
    'line-width': 2,
  },
  type: 'line',
});

export const textLayer = (
  isDrawLineLayer: boolean,
  fontObj: { id: string; mapboxValue: string },
  fontWeightObj: { id: string; label: string }
) => ({
  filter: [
    'all',
    ['==', appendUser(isDrawLineLayer, 'toolId'), 'text'],
    ['==', appendUser(isDrawLineLayer, 'font-type'), fontObj.id],
    ['==', appendUser(isDrawLineLayer, 'font-weight'), fontWeightObj.id],
    ['!=', appendUser(isDrawLineLayer, 'hidden'), true],
  ],
  glyphs: 'mapbox://fonts/acres-mapbox/{fontstack}/{range}.pbf',
  id: `${CUSTOMIZE_PREPENDED_ID}-text-${fontObj.id}-${fontWeightObj.id}`,
  layout: {
    'icon-allow-overlap': true,
    'icon-image': 'text-box-draw',
    'icon-text-fit': 'both',
    'icon-text-fit-padding': [6, 10, 6, 10],
    'symbol-sort-key': 100,
    'text-allow-overlap': true,
    'text-field': ['get', appendUser(isDrawLineLayer, 'name')],
    'text-font': [
      `${fontObj.mapboxValue} ${fontWeightObj.label}`,
      'Arial Unicode MS Regular',
    ],
    'text-size': ['coalesce', ['get', appendUser(isDrawLineLayer, 'size')], 20],
  },
  paint: {
    'icon-color': [
      'coalesce',
      ['get', appendUser(isDrawLineLayer, 'border-color')],
      customColor.black,
    ],
    'icon-opacity': [
      'case',
      [
        '==',
        ['get', appendUser(isDrawLineLayer, 'border-color')],
        'transparent',
      ],
      0,
      ['get', appendUser(isDrawLineLayer, 'border-opacity')],
    ],
    'text-color': [
      'coalesce',
      ['get', appendUser(isDrawLineLayer, 'color')],
      customColor.white,
    ],
    'text-halo-color': customColor.black,
    'text-halo-width': 1.25,
    'text-opacity': [
      'case',
      ['==', ['get', appendUser(isDrawLineLayer, 'color')], 'transparent'],
      0,
      ['get', appendUser(isDrawLineLayer, 'opacity')],
    ],
  },
  type: 'symbol',
});

export const textLayers = (isDrawLineLayer: boolean) =>
  Object.values(fontWeightMap)
    .map((fontWeightObj) => {
      return Object.values(fontTypesMap).map((fontType) =>
        textLayer(isDrawLineLayer, fontType, fontWeightObj)
      );
    })
    .flat();

export const polygonFillPaint = (isDrawLayer: boolean) => ({
  'fill-color': [
    'coalesce',
    ['get', appendUser(isDrawLayer, 'color')],
    customColor.white,
  ],
  'fill-opacity': [
    'coalesce',
    ['get', appendUser(isDrawLayer, 'opacity')],
    0.2,
  ],
});
